import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Image, Link as LinkIcon, Video, Music, File, Play, X,
  AlertTriangle, Check, Heart, Book, ShoppingBag, Quote, MessageCircle,
  Youtube, Twitter, Instagram, Linkedin, FileImage, FileCode
} from 'lucide-react';
import { logUserInteraction } from '../../services/behaviorTracker';
import { useAuth } from '../../contexts/AuthContext';
import { getThumbnailUrl, isVideoUrl, convertVideoThumbnailToStatic } from '../../utils/thumbnailFallbacks';
import { getImageUrl } from '../../utils/imageProxy';
import { eventsService } from '../../services/eventsService';
import { supabase } from '../../lib/supabase';
import { useAskSwirlStore } from '../../stores/askSwirlStore';

/**
 * Smart Content Card - Renders different layouts based on content type
 * 
 * Supported types:
 * - book: Book cards with cover, author, ISBN
 * - product: Product cards with price, brand, rating
 * - quote: Quote cards with styled text and attribution
 * - note: Note cards (text-focused)
 * - twitter/linkedin/instagram: Social media posts
 * - ebook/pdf/document: Document previews
 * - image/video: Media cards
 */

const TYPE_ICONS = {
  'book': Book,
  'product': ShoppingBag,
  'quote': Quote,
  'note': FileText,
  'twitter': Twitter,
  'linkedin': Linkedin,
  'instagram': Instagram,
  'youtube': Youtube,
  'image': Image,
  'screenshot': FileImage,
  'video': Video,
  'audio': Music,
  'pdf': File,
  'ebook': Book,
  'document': FileText,
  'code': FileCode,
  'link': LinkIcon,
  'web_clip': LinkIcon,
};

const PLATFORM_COLORS = {
  'twitter': { bg: 'bg-blue-500', text: 'text-blue-500', badge: 'bg-blue-100' },
  'linkedin': { bg: 'bg-blue-700', text: 'text-blue-700', badge: 'bg-blue-100' },
  'instagram': { bg: 'bg-pink-500', text: 'text-pink-500', badge: 'bg-pink-100' },
  'youtube': { bg: 'bg-red-600', text: 'text-red-600', badge: 'bg-red-100' },
  'tiktok': { bg: 'bg-black', text: 'text-black', badge: 'bg-gray-100' },
};

function extractDomain(url) {
  if (!url) return '';
  try {
    const urlObj = new URL(url);
    return urlObj.hostname.replace('www.', '');
  } catch {
    return '';
  }
}

function decodeHtmlEntities(input) {
  if (!input || typeof input !== 'string') return input;
  let s = input
    .replaceAll('&quot;', '"')
    .replaceAll('&apos;', "'")
    .replaceAll('&#39;', "'")
    .replaceAll('&amp;', '&')
    .replaceAll('&lt;', '<')
    .replaceAll('&gt;', '>');
  s = s.replace(/&#x([0-9a-fA-F]+);/g, (_m, hex) => {
    try {
      return String.fromCodePoint(parseInt(hex, 16));
    } catch {
      return _m;
    }
  });
  s = s.replace(/&#([0-9]+);/g, (_m, num) => {
    try {
      return String.fromCodePoint(parseInt(num, 10));
    } catch {
      return _m;
    }
  });
  return s;
}

export default function SmartContentCard({
  item,
  onClick,
  onDelete,
  sourceSwirlId = null,
  query = '',
  context = 'swirlspace',
  isBulkSelectMode = false,
  isSelected = false,
  onBulkSelect = null,
  matchReasons = null
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { addChip, clearChips } = useAskSwirlStore();
  const [imageLoading, setImageLoading] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [togglingFavorite, setTogglingFavorite] = useState(false);
  const [isImageVisible, setIsImageVisible] = useState(false); // Visibility-gated image loading
  const imgRef = useRef(null);
  const cardRef = useRef(null);
  const imageContainerRef = useRef(null); // Ref for image container to observe visibility
  const impressionTracked = useRef(false);
  const dwellStartTime = useRef(null);

  const sourceType = item.source_type?.toLowerCase() || 'web_clip';
  const Icon = TYPE_ICONS[sourceType] || FileText;
  const domain = item.url ? extractDomain(item.url) : '';

  // Extract metadata from item.meta
  const meta = item.meta || {};
  const getUnifiedTags = (it) => {
    const m = it?.meta || {};
    const deriveTagsFromText = (text, max = 3) => {
      if (!text || typeof text !== 'string') return [];
      const stop = new Set([
        'the','and','for','with','that','this','from','your','you','are','was','were','has','have','had','not','but',
        'they','their','them','his','her','she','him','its','our','out','into','about','over','under','more','most',
        'what','when','where','why','how','can','could','should','would','will','just','than','then','also',
        'video','youtube','instagram','tiktok','twitter','linkedin',
      ]);
      const tokens = text
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, ' ')
        .split(/\s+/)
        .map((t) => t.trim())
        .filter((t) => t.length >= 4 && !stop.has(t));
      const counts = new Map();
      for (const t of tokens) counts.set(t, (counts.get(t) || 0) + 1);
      const sorted = [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([t]) => t);
      const picked = sorted.slice(0, max).map((t) => t.replace(/^\w/, (c) => c.toUpperCase()));
      return Array.from(new Set(picked));
    };

    const candidates = [
      it?.primary_tags,
      it?.auto_tags?.topics,
      m?.ai_analysis?.tags,
      it?.analysis_data?.tags,
      Array.isArray(it?.tags) ? it.tags : null,
    ];
    for (const c of candidates) {
      if (Array.isArray(c)) {
        const cleaned = c.filter(Boolean);
        if (cleaned.length > 0) return cleaned;
      }
    }
    // Last-resort fallbacks (no AI): use any existing categorical metadata
    // so cards still show *something* instead of blank.
    const fallback = [];
    if (typeof m?.topic_name === 'string' && m.topic_name.trim()) fallback.push(m.topic_name.trim());
    if (typeof m?.topic === 'string' && m.topic.trim()) fallback.push(m.topic.trim());
    if (Array.isArray(m?.categories)) fallback.push(...m.categories.filter(Boolean));
    if (typeof it?.channel === 'string' && it.channel.trim() && it.channel !== 'YouTube') fallback.push(it.channel.trim());
    const metaSummary =
      (typeof m?.ai_analysis?.summary === 'string' ? m.ai_analysis.summary : null) ||
      (typeof it?.description === 'string' ? it.description : null) ||
      '';
    const derived = deriveTagsFromText(`${it?.title || ''} ${metaSummary}`, 3);
    return Array.from(new Set([...fallback, ...derived])).slice(0, 4);
  };
  const isBook = sourceType === 'book' || sourceType === 'ebook';
  const isProduct = sourceType === 'product';
  const isQuote = sourceType === 'quote';
  const isNote = sourceType === 'note';
  // DISABLED: Social card layout was causing inconsistent UI for Instagram/YouTube/TikTok
  // These should use the default card layout with channel badge instead of social header
  // const isSocial = ['twitter', 'linkedin', 'instagram', 'youtube', 'tiktok'].includes(sourceType);
  const isSocial = false; // Use default card layout for all social content - cleaner, consistent design
  const isDocument = ['pdf', 'ebook', 'document'].includes(sourceType);

  // #region agent log
  useEffect(() => {
    try {
      const title = typeof item?.title === 'string' ? item.title.slice(0, 80) : item?.title;
      const thumb = typeof item?.thumbnail_url === 'string' ? item.thumbnail_url.slice(0, 120) : item?.thumbnail_url;
      const img = typeof item?.image_url === 'string' ? item.image_url.slice(0, 120) : item?.image_url;
      const metaThumb = typeof item?.meta?.thumbnail_url === 'string' ? item.meta.thumbnail_url.slice(0, 120) : item?.meta?.thumbnail_url;
      const analysisThumb = typeof item?.analysis_data?.thumbnail_url === 'string' ? item.analysis_data.thumbnail_url.slice(0, 120) : item?.analysis_data?.thumbnail_url;
      // Debug logging removed
    } catch {}
  }, [item?.id]);
  // #endregion agent log

  // Check favorite status
  useEffect(() => {
    if (!user?.id || !item?.id) return;
    const checkFavorite = async () => {
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/d63f98f2-a476-45f2-8f18-652bfd36f226',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'SmartContentCard.jsx:200',message:'checkFavorite entry',data:{userId:user?.id,itemId:item?.id},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'B'})}).catch(()=>{});
      // #endregion
      try {
        const { data, error } = await supabase
          .schema('deepsearch')
          .from('behavioral_signals')
          .select('id')
          .eq('user_id', user.id)
          .eq('item_id', item.id)
          .eq('interaction_type', 'like')
          .limit(1)
          .maybeSingle();
        // #region agent log
        fetch('http://127.0.0.1:7243/ingest/d63f98f2-a476-45f2-8f18-652bfd36f226',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'SmartContentCard.jsx:211',message:'checkFavorite result',data:{hasError:!!error,errorCode:error?.code,hasData:!!data},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'B'})}).catch(()=>{});
        // #endregion
        // Silently handle 403 errors (RLS policy issues)
        if (error && (error.code === 'PGRST301' || error.message?.includes('policy'))) {
          setIsFavorited(false);
          return;
        }
        if (!error && data) setIsFavorited(true);
      } catch (error) {
        // Silently ignore all errors
      }
    };
    checkFavorite();
  }, [user?.id, item?.id]);

  // Track impression
  useEffect(() => {
    if (!cardRef.current || impressionTracked.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !impressionTracked.current) {
            impressionTracked.current = true;
            const tags = getUnifiedTags(item);
            const colors = item.primary_color ? [item.primary_color] : item.meta?.colors || [];
            eventsService.trackImpression(item.id, query, tags, colors);
          }
        });
      },
      { threshold: 0.5 }
    );
    observer.observe(cardRef.current);
    return () => {
      if (cardRef.current) observer.unobserve(cardRef.current);
    };
  }, [item.id, query]);

  // Thumbnail handling - use direct URLs, no risky memoization
  const rawThumbnailUrl = getThumbnailUrl(item);
  const rawImageUrl = item.image_url || null;
  
  // Get processed thumbnail URL (with proxy if needed)
  const processedThumbnailUrl = rawThumbnailUrl 
    ? getImageUrl({ image: rawThumbnailUrl, thumbnail_url: rawThumbnailUrl })
    : null;
  
  // Fallback to image_url if thumbnail_url is missing
  const fallbackImageUrl = rawImageUrl 
    ? getImageUrl({ image: rawImageUrl, thumbnail_url: rawImageUrl })
    : null;
  
  const [thumbnailUrl, setThumbnailUrl] = useState(processedThumbnailUrl);
  const [imageFailed, setImageFailed] = useState(false);

  // Visibility-gated image loading: only load images when card is visible in viewport
  // Observer attached to wrapper div that ALWAYS renders (not dependent on thumbnailUrl)
  useEffect(() => {
    if (!imageContainerRef.current) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // One-way visibility: once true, stay true forever
            setIsImageVisible(true);
            // Unobserve once visible to reduce overhead
            observer.unobserve(entry.target);
          }
        });
      },
      { 
        rootMargin: '50px', // Start loading 50px before entering viewport
        threshold: 0.01 
      }
    );
    
    observer.observe(imageContainerRef.current);
    
    return () => {
      if (imageContainerRef.current) {
        observer.unobserve(imageContainerRef.current);
      }
    };
  }, []); // Empty deps - observer attaches once and stays

  // Update thumbnailUrl when processedThumbnailUrl changes (handles TikTok video thumbnails)
  useEffect(() => {
    if (processedThumbnailUrl) {
      setThumbnailUrl(processedThumbnailUrl);
    } else if (fallbackImageUrl) {
      setThumbnailUrl(fallbackImageUrl);
    } else {
      setThumbnailUrl(null);
    }
  }, [processedThumbnailUrl, fallbackImageUrl]);

  // Handle TikTok video thumbnail conversion
  useEffect(() => {
    if (rawThumbnailUrl && item.channel === 'TikTok' && isVideoUrl(rawThumbnailUrl)) {
      const proxiedUrl = getImageUrl({ image: rawThumbnailUrl, thumbnail_url: rawThumbnailUrl });
      convertVideoThumbnailToStatic(proxiedUrl).then(staticImageUrl => {
        setThumbnailUrl(staticImageUrl);
      }).catch(error => {
        console.warn('Failed to convert TikTok video thumbnail to static:', error);
        setThumbnailUrl(proxiedUrl);
      });
    }
  }, [rawThumbnailUrl, item.channel]);

  // Reset image failed state when URL changes
  useEffect(() => {
    setImageFailed(false);
  }, [thumbnailUrl]);

  const handleClick = (e) => {
    if (isBulkSelectMode && onBulkSelect) {
      e.stopPropagation();
      onBulkSelect(item.id, !isSelected);
      return;
    }
    if (user?.id && item.id) {
      logUserInteraction(user.id, item.id, 'view', sourceSwirlId).catch(() => {});
    }
    const tags = item.primary_tags || item.auto_tags?.topics || item.tags || [];
    const colors = item.primary_color ? [item.primary_color] : item.meta?.colors || [];
    eventsService.trackClick(item.id, query, tags, colors);
    dwellStartTime.current = Date.now();
    if (onClick) onClick(item);
  };

  const handleMouseLeave = () => {
    if (dwellStartTime.current) {
      const dwellMs = Date.now() - dwellStartTime.current;
      if (dwellMs > 1000) {
        const tags = item.primary_tags || item.auto_tags?.topics || item.tags || [];
        const colors = item.primary_color ? [item.primary_color] : item.meta?.colors || [];
        eventsService.trackDwell(item.id, dwellMs, query, tags, colors);
      }
      dwellStartTime.current = null;
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (onDelete) onDelete(item);
  };

  const handleFavorite = async (e) => {
    e.stopPropagation();
    if (!user?.id || !item?.id || togglingFavorite) return;
    setTogglingFavorite(true);
    try {
      if (isFavorited) {
        const { error } = await supabase
          .schema('deepsearch')
          .from('behavioral_signals')
          .delete()
          .eq('user_id', user.id)
          .eq('item_id', item.id)
          .eq('interaction_type', 'like');
        if (error) throw error;
        setIsFavorited(false);
        await logUserInteraction(user.id, item.id, 'dismiss', sourceSwirlId).catch(() => {});
      } else {
        const { error } = await supabase
          .schema('deepsearch')
          .from('behavioral_signals')
          .insert({
            user_id: user.id,
            item_id: item.id,
            interaction_type: 'like',
            source_swirl_id: sourceSwirlId || null,
          });
        if (error) throw error;
        setIsFavorited(true);
        await logUserInteraction(user.id, item.id, 'like', sourceSwirlId).catch(() => {});
      }
      window.dispatchEvent(new CustomEvent('favoriteChanged', {
        detail: { itemId: item.id, isFavorited: !isFavorited }
      }));
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      setIsFavorited(!isFavorited);
    } finally {
      setTogglingFavorite(false);
    }
  };

  // BOOK CARD LAYOUT
  if (isBook) {
    const bookSummary =
      item.analysis_data?.summary ||
      meta.ai_analysis?.summary ||
      meta.bookDescription ||
      item.description ||
      '';

    return (
      <motion.article
        ref={cardRef}
        onClick={handleClick}
        onMouseLeave={handleMouseLeave}
        whileHover={{ scale: 1.02, y: -4 }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
        className={`rounded-xl border transition-all overflow-hidden group cursor-pointer relative flex flex-col ${
          isBulkSelectMode && isSelected
            ? 'border-purple-500 bg-purple-50/70 shadow-lg ring-2 ring-purple-300'
            : 'border-neutral-200 bg-white/70 backdrop-blur shadow-sm hover:shadow-lg'
        }`}
        style={{ boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)' }}
      >
        {/* Book Cover - aspect-video like other cards */}
        <div ref={imageContainerRef} className="aspect-video bg-neutral-100 relative overflow-hidden flex items-center justify-center">
          {isImageVisible && (thumbnailUrl || fallbackImageUrl) && !imageFailed ? (
            <img
              ref={imgRef}
              src={thumbnailUrl || fallbackImageUrl}
              alt={meta.title || item.title || 'Book cover'}
              className="h-full object-contain"
              loading="lazy"
              referrerPolicy="no-referrer"
              onLoad={() => setImageLoading(false)}
              onError={(e) => {
                setImageLoading(false);
                // Retry once with fallback image_url if thumbnail_url failed
                if (thumbnailUrl && fallbackImageUrl && thumbnailUrl !== fallbackImageUrl) {
                  if (import.meta.env.DEV) {
                    console.warn(`🖼️ [Card ${item.id}] Thumbnail failed, retrying with image_url:`, thumbnailUrl);
                  }
                  setThumbnailUrl(fallbackImageUrl);
                } else {
                  // Both failed, show placeholder
                  setImageFailed(true);
                  if (import.meta.env.DEV) {
                    console.warn(`🖼️ [Card ${item.id}] Image failed to load:`, thumbnailUrl || fallbackImageUrl);
                  }
                }
              }}
            />
          ) : isImageVisible && imageFailed ? (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-amber-100 to-orange-200">
              <Book className="w-12 h-12 text-amber-600" />
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-neutral-100" />
          )}

          {/* Delete Button - Top Left on hover */}
          {onDelete && !isBulkSelectMode && (
            <button
              onClick={handleDelete}
              className="absolute top-2 left-2 p-1.5 bg-white/90 hover:bg-white text-red-600 hover:text-red-700 rounded-lg shadow-md transition-all opacity-0 group-hover:opacity-100 z-20"
            >
              <X size={14} />
            </button>
          )}
          
          {/* Type Badge - Top Right */}
          <div className="absolute top-2 right-2 z-20">
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
              Book
            </span>
          </div>

          {/* Favorite Button - Bottom Right */}
          <button
            onClick={handleFavorite}
            disabled={togglingFavorite}
            className={`absolute bottom-2 right-2 p-2 rounded-full shadow-lg transition-all z-30 ${
              isFavorited
                ? 'bg-white text-red-500'
                : 'bg-white/90 text-neutral-400 hover:text-red-500 hover:bg-white'
            }`}
          >
            <Heart className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`} />
          </button>
        </div>

        {/* Content Section - Exactly like default cards */}
        <div className="p-3 flex flex-col flex-grow">
          {/* Title - prioritize bookTitle, then title if not a URL */}
          <h3 className="font-medium text-neutral-900 text-sm line-clamp-2 mb-1">
            {meta.bookTitle || 
             (item.title && !item.title.startsWith('http') && !item.title.includes('amazon.com') ? item.title : null) || 
             'Untitled Book'}
          </h3>
          
          {/* Author line */}
          {meta.author && (
            <p className="text-xs text-neutral-500 mb-1">
              by {meta.author}
            </p>
          )}
          
          {/* Description - show bookDescription or description if not generic */}
          {(bookSummary && !bookSummary?.startsWith?.('Added to') && !bookSummary?.includes?.('amazon.com')) && (
            <p className="text-xs text-neutral-600 line-clamp-2 mb-2">
              {bookSummary}
            </p>
          )}

          {/* Tags and Date Row */}
          <div className="mt-auto flex items-center justify-between text-xs">
            {(() => {
              // Get tags from multiple possible locations (same as default card)
              const tags = item.auto_tags?.topics || 
                          item.analysis_data?.tags ||
                          item.meta?.categories ||
                          (Array.isArray(item.tags) ? item.tags : []);
              return tags.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {tags.slice(0, 2).map((tag, i) => (
                    <span key={i} className="px-1.5 py-0.5 bg-neutral-100 text-neutral-600 text-xs rounded">
                      {tag}
                    </span>
                  ))}
                </div>
              ) : (
                <div />
              );
            })()}
            {item.created_at && (
              <span className="text-neutral-400">
                {new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            )}
          </div>
        </div>
      </motion.article>
    );
  }

  // PRODUCT CARD LAYOUT - MATCHES BOOK CARD DESIGN
  if (isProduct) {
    // Extract product summary safely
    const productSummary = 
      item.meta?.analysis_data?.summary || 
      item.meta?.ai_analysis?.summary || 
      item.analysis_data?.summary || 
      item.meta?.description || 
      item.description;

    return (
      <motion.article
        ref={cardRef}
        onClick={handleClick}
        onMouseLeave={handleMouseLeave}
        whileHover={{ scale: 1.02, y: -4 }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
        className={`rounded-xl border transition-all overflow-hidden group cursor-pointer relative flex flex-col ${
          isBulkSelectMode && isSelected
            ? 'border-purple-500 bg-purple-50/70 shadow-lg ring-2 ring-purple-300'
            : 'border-neutral-200 bg-white/70 backdrop-blur shadow-sm hover:shadow-lg'
        }`}
        style={{ boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)' }}
      >
        {/* Product Image - MATCHES DEFAULT CARD */}
        <div ref={imageContainerRef} className="aspect-video bg-neutral-100 relative overflow-hidden flex items-center justify-center flex-shrink-0">
          {isImageVisible && (thumbnailUrl || fallbackImageUrl) && !imageFailed ? (
            <img
              ref={imgRef}
              src={thumbnailUrl || fallbackImageUrl}
              alt={meta.name || item.title || 'Product'}
              className="h-full object-contain"
              loading="lazy"
              referrerPolicy="no-referrer"
              onLoad={() => setImageLoading(false)}
              onError={(e) => {
                setImageLoading(false);
                // Retry once with fallback image_url if thumbnail_url failed
                if (thumbnailUrl && fallbackImageUrl && thumbnailUrl !== fallbackImageUrl) {
                  if (import.meta.env.DEV) {
                    console.warn(`🖼️ [Card ${item.id}] Product thumbnail failed, retrying with image_url:`, thumbnailUrl);
                  }
                  setThumbnailUrl(fallbackImageUrl);
                } else {
                  setImageFailed(true);
                  if (import.meta.env.DEV) {
                    console.warn(`🖼️ [Card ${item.id}] Product image failed to load:`, thumbnailUrl || fallbackImageUrl);
                  }
                }
              }}
            />
          ) : isImageVisible && imageFailed ? (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-green-100 to-emerald-200">
              <ShoppingBag className="w-12 h-12 text-green-600" />
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-neutral-100" />
          )}

          {/* Delete Button - TOP LEFT on hover (like book) */}
          {onDelete && !isBulkSelectMode && (
            <button
              onClick={handleDelete}
              className="absolute top-2 left-2 p-1.5 bg-white/90 hover:bg-white text-red-600 hover:text-red-700 rounded-lg shadow-md transition-all opacity-0 group-hover:opacity-100 z-20"
            >
              <X size={14} />
            </button>
          )}
          
          {/* Type Badge - TOP RIGHT (like book) */}
          <div className="absolute top-2 right-2 z-20">
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Product
            </span>
          </div>

          {/* Favorite Button - BOTTOM RIGHT on image (like default cards) */}
          <button
            onClick={handleFavorite}
            disabled={togglingFavorite}
            className={`absolute bottom-2 right-2 p-2 rounded-full shadow-lg transition-all z-30 ${
              isFavorited
                ? 'bg-red-500 text-white hover:bg-red-600'
                : 'bg-white/90 text-neutral-400 hover:text-red-500 hover:bg-white'
            }`}
          >
            <Heart className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`} />
          </button>
        </div>

        {/* Content Section - MATCHES BOOK CARD EXACTLY */}
        <div className="p-3 flex flex-col flex-grow">
          {/* Title - prioritize meta.name */}
          <h3 className="font-medium text-neutral-900 text-sm line-clamp-2 mb-1">
            {meta.name || 
             (item.title && !item.title.startsWith('http') && !item.title.includes('amazon.com') ? item.title : null) || 
             'Untitled Product'}
          </h3>
          
          {/* Brand line (like "by Author" for books) */}
          {meta.brand && (
            <p className="text-xs text-neutral-500 mb-1">
              by {meta.brand}
            </p>
          )}
          
          {/* Description - show summary if available and not generic */}
          {(productSummary && !productSummary?.startsWith?.('Added to') && !productSummary?.includes?.('amazon.com')) && (
            <p className="text-xs text-neutral-600 line-clamp-2 mb-2">
              {productSummary}
            </p>
          )}

          {/* Tags and Date Row - MATCHES BOOK CARD */}
          <div className="mt-auto flex items-center justify-between text-xs">
            {(() => {
              const tags = getUnifiedTags(item);
              return tags.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {tags.slice(0, 2).map((tag, idx) => (
                    <span key={idx} className="px-1.5 py-0.5 bg-neutral-100 text-neutral-600 text-xs rounded">
                      {tag}
                    </span>
                  ))}
                </div>
              ) : (
                <div />
              );
            })()}
            {item.created_at && (
              <span className="text-neutral-400">
                {new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            )}
          </div>
        </div>
      </motion.article>
    );
  }

  // QUOTE CARD LAYOUT
  if (isQuote) {
    return (
      <motion.article
        ref={cardRef}
        onClick={handleClick}
        onMouseLeave={handleMouseLeave}
        whileHover={{ scale: 1.02, y: -4 }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
        className={`rounded-xl border transition-all overflow-hidden group cursor-pointer relative ${
          isBulkSelectMode && isSelected
            ? 'border-purple-500 bg-purple-50/70 shadow-lg ring-2 ring-purple-300'
            : 'border-neutral-200 bg-gradient-to-br from-purple-50 to-pink-50 backdrop-blur shadow-sm hover:shadow-lg'
        }`}
        style={{ boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)' }}
      >
        <div className="p-5">
          {/* Quote Badge */}
          <div className="flex items-center gap-2 mb-3">
            <Quote className="w-5 h-5 text-purple-600" />
            <span className="text-xs font-medium text-purple-600 uppercase tracking-wide">
              Quote
            </span>
          </div>

          {/* Quote Text */}
          <blockquote className="text-base font-serif text-neutral-800 italic line-clamp-4 mb-3 pl-4 border-l-4 border-purple-400">
            "{item.description || item.raw_text || item.title || 'Quote'}"
          </blockquote>

          {/* Attribution */}
          {(meta.author || meta.publication || domain) && (
            <p className="text-sm text-neutral-600">
              — {meta.author || meta.publication || domain}
            </p>
          )}

          {/* Source Page */}
          {meta.pageTitle && (
            <p className="text-xs text-neutral-400 mt-2">
              from "{meta.pageTitle}"
            </p>
          )}

          {/* Favorite Button */}
          <button
            onClick={handleFavorite}
            disabled={togglingFavorite}
            className={`absolute top-2 right-2 p-2 rounded-full shadow-lg transition-all z-30 ${
              isFavorited
                ? 'bg-red-500 text-white hover:bg-red-600'
                : 'bg-white/90 text-neutral-400 hover:text-red-500 hover:bg-white'
            }`}
          >
            <Heart className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`} />
          </button>

          {/* Delete Button */}
          {onDelete && !isBulkSelectMode && (
            <button
              onClick={handleDelete}
              className="absolute bottom-2 right-2 p-1.5 bg-white/90 hover:bg-white text-red-600 hover:text-red-700 rounded-lg shadow-md transition-all opacity-0 group-hover:opacity-100 z-20"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </motion.article>
    );
  }

  // SOCIAL MEDIA CARD LAYOUT
  if (isSocial) {
    const platformConfig = PLATFORM_COLORS[sourceType] || { bg: 'bg-blue-500', text: 'text-blue-500', badge: 'bg-blue-100' };
    const PlatformIcon = TYPE_ICONS[sourceType] || MessageCircle;

    return (
      <motion.article
        ref={cardRef}
        onClick={handleClick}
        onMouseLeave={handleMouseLeave}
        whileHover={{ scale: 1.02, y: -4 }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
        className={`rounded-xl border transition-all overflow-hidden group cursor-pointer relative flex flex-col ${
          isBulkSelectMode && isSelected
            ? 'border-purple-500 bg-purple-50/70 shadow-lg ring-2 ring-purple-300'
            : 'border-neutral-200 bg-white/70 backdrop-blur shadow-sm hover:shadow-lg'
        }`}
        style={{ boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)' }}
      >
        {/* Platform Header */}
        <div className="p-3 border-b border-neutral-100">
          <div className="flex items-center gap-2">
            <div className={`p-2 rounded-lg ${platformConfig.badge}`}>
              <PlatformIcon className={`w-4 h-4 ${platformConfig.text}`} />
            </div>
            <div>
              <p className="text-sm font-semibold text-neutral-800 capitalize">
                {sourceType}
              </p>
              {meta.author && (
                <p className="text-xs text-neutral-500">
                  @{meta.author}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Content/Thumbnail - wrapper always renders for observer */}
        <div ref={imageContainerRef} className="aspect-video bg-neutral-100 relative overflow-hidden">
          {isImageVisible && (thumbnailUrl || fallbackImageUrl) && !imageFailed ? (
            <>
              <img
                ref={imgRef}
                src={thumbnailUrl || fallbackImageUrl}
                alt={item.title || ''}
                className="w-full h-full object-cover"
                loading="lazy"
                referrerPolicy="no-referrer"
                onError={(e) => {
                  // Retry once with fallback image_url if thumbnail_url failed
                  if (thumbnailUrl && fallbackImageUrl && thumbnailUrl !== fallbackImageUrl) {
                    if (import.meta.env.DEV) {
                      console.warn(`🖼️ [Card ${item.id}] Social thumbnail failed, retrying with image_url:`, thumbnailUrl);
                    }
                    setThumbnailUrl(fallbackImageUrl);
                  } else {
                    setImageFailed(true);
                    if (import.meta.env.DEV) {
                      console.warn(`🖼️ [Card ${item.id}] Social image failed to load:`, thumbnailUrl || fallbackImageUrl);
                    }
                  }
                }}
              />
              {sourceType === 'youtube' && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
                  <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                    <Play className="w-8 h-8 text-neutral-900 ml-1" fill="currentColor" />
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="w-full h-full bg-neutral-100" />
          )}
        </div>

        {/* Post Content */}
        <div className="p-3 flex flex-col">
          <p className="text-sm text-neutral-800 line-clamp-3 mb-2">
            {meta.content || meta.description || item.description || item.title}
          </p>

          {/* Engagement Stats */}
          {(meta.views || meta.likes || meta.comments) && (
            <div className="flex items-center gap-3 text-xs text-neutral-500">
              {meta.views && <span>👁 {meta.views}</span>}
              {meta.likes && <span>❤️ {meta.likes}</span>}
              {meta.comments && <span>💬 {meta.comments}</span>}
            </div>
          )}
        </div>

        {/* Favorite Button */}
        <button
          onClick={handleFavorite}
          disabled={togglingFavorite}
          className={`absolute top-2 right-2 p-2 rounded-full shadow-lg transition-all z-30 ${
            isFavorited
              ? 'bg-red-500 text-white hover:bg-red-600'
              : 'bg-white/90 text-neutral-400 hover:text-red-500 hover:bg-white'
          }`}
        >
          <Heart className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`} />
        </button>

        {/* Delete Button */}
        {onDelete && !isBulkSelectMode && (
          <button
            onClick={handleDelete}
            className="absolute bottom-2 right-2 p-1.5 bg-white/90 hover:bg-white text-red-600 hover:text-red-700 rounded-lg shadow-md transition-all opacity-0 group-hover:opacity-100 z-20"
          >
            <X size={14} />
          </button>
        )}
      </motion.article>
    );
  }

  // NOTE CARD LAYOUT (Text-only)
  if (isNote) {
    return (
      <motion.article
        ref={cardRef}
        onClick={handleClick}
        onMouseLeave={handleMouseLeave}
        whileHover={{ scale: 1.02, y: -4 }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
        className={`rounded-xl border transition-all overflow-hidden group cursor-pointer relative ${
          isBulkSelectMode && isSelected
            ? 'border-purple-500 bg-purple-50/70 shadow-lg ring-2 ring-purple-300'
            : 'border-neutral-200 bg-yellow-50/70 backdrop-blur shadow-sm hover:shadow-lg'
        }`}
        style={{ boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)' }}
      >
        <div className="p-4">
          {/* Note Badge */}
          <div className="flex items-center gap-2 mb-3">
            <FileText className="w-4 h-4 text-yellow-700" />
            <span className="text-xs font-medium text-yellow-700 uppercase tracking-wide">
              Note
            </span>
          </div>

          {/* Title */}
          <h4 className="text-base font-semibold text-neutral-900 mb-2 line-clamp-1">
            {item.title || 'Untitled Note'}
          </h4>

          {/* Note Content */}
          <p className="text-sm text-neutral-700 line-clamp-6 whitespace-pre-wrap">
            {item.description || item.raw_text || 'No content'}
          </p>

          {/* Date */}
          {item.created_at && (
            <p className="text-xs text-neutral-400 mt-3">
              {new Date(item.created_at).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              })}
            </p>
          )}

          {/* Favorite Button */}
          <button
            onClick={handleFavorite}
            disabled={togglingFavorite}
            className={`absolute top-2 right-2 p-2 rounded-full shadow-lg transition-all z-30 ${
              isFavorited
                ? 'bg-red-500 text-white hover:bg-red-600'
                : 'bg-white/90 text-neutral-400 hover:text-red-500 hover:bg-white'
            }`}
          >
            <Heart className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`} />
          </button>

          {/* Delete Button */}
          {onDelete && !isBulkSelectMode && (
            <button
              onClick={handleDelete}
              className="absolute bottom-2 right-2 p-1.5 bg-white/90 hover:bg-white text-red-600 hover:text-red-700 rounded-lg shadow-md transition-all opacity-0 group-hover:opacity-100 z-20"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </motion.article>
    );
  }

  // DEFAULT CARD LAYOUT (For all other types: images, videos, web_clips, documents, etc.)
  // This is the existing ContentCard.jsx logic
  const typeColor = 
    sourceType === 'image' || sourceType === 'screenshot' ? 'bg-purple-100 text-purple-800' :
    sourceType === 'video' ? 'bg-red-100 text-red-800' :
    sourceType === 'pdf' || isDocument ? 'bg-orange-100 text-orange-800' :
    sourceType === 'audio' ? 'bg-yellow-100 text-yellow-800' :
    'bg-emerald-100 text-emerald-800';

  return (
    <motion.article
      ref={cardRef}
      onClick={handleClick}
      onMouseLeave={handleMouseLeave}
      whileHover={{ scale: 1.02, y: -4 }}
      transition={{ duration: 0.2, ease: 'easeInOut' }}
      className={`rounded-xl border transition-all overflow-hidden group cursor-pointer relative flex flex-col w-full ${
        isBulkSelectMode && isSelected
          ? 'border-purple-500 bg-purple-50/70 shadow-lg ring-2 ring-purple-300'
          : 'border-neutral-200 bg-white/70 backdrop-blur shadow-sm hover:shadow-lg'
      }`}
      style={{ boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)' }}
    >
      {/* Bulk Select Checkbox */}
      {isBulkSelectMode && (
        <div
          className="absolute top-2 left-2 z-30 cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            if (onBulkSelect) onBulkSelect(item.id, !isSelected);
          }}
        >
          <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all shadow-lg ${
            isSelected
              ? 'bg-purple-600 border-purple-600'
              : 'bg-white/95 border-neutral-300 hover:border-purple-400 hover:bg-white'
          }`}>
            {isSelected && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
          </div>
        </div>
      )}

      {/* Thumbnail/Preview Section - wrapper always renders for observer */}
      <div ref={imageContainerRef} className="aspect-video bg-neutral-100 relative overflow-hidden group flex-shrink-0">
        {imageLoading && isImageVisible && (thumbnailUrl || fallbackImageUrl) && !imageFailed && (
          <div className="absolute inset-0 bg-neutral-100/90 backdrop-blur-[2px] flex items-center justify-center z-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto mb-2"></div>
              <p className="text-xs text-neutral-600">Loading...</p>
            </div>
          </div>
        )}

        {isImageVisible && (thumbnailUrl || fallbackImageUrl) && !imageFailed ? (
          <img
            ref={imgRef}
            src={thumbnailUrl || fallbackImageUrl}
            alt={item.title || ''}
            className={`w-full h-full object-cover transition-opacity duration-300 ${
              imageLoading ? 'opacity-0' : 'opacity-100'
            }`}
            loading="lazy"
            referrerPolicy="no-referrer"
            onLoadStart={() => setImageLoading(true)}
            onLoad={() => setImageLoading(false)}
            onError={(e) => {
              setImageLoading(false);
              // Retry once with fallback image_url if thumbnail_url failed
              if (thumbnailUrl && fallbackImageUrl && thumbnailUrl !== fallbackImageUrl) {
                if (import.meta.env.DEV) {
                  console.warn(`🖼️ [Card ${item.id}] Thumbnail failed, retrying with image_url:`, thumbnailUrl);
                }
                setThumbnailUrl(fallbackImageUrl);
              } else {
                setImageFailed(true);
                if (import.meta.env.DEV) {
                  console.warn(`🖼️ [Card ${item.id}] Image failed to load:`, thumbnailUrl || fallbackImageUrl);
                }
              }
            }}
          />
        ) : (
          <div className="w-full h-full bg-neutral-100" />
        )}

        {/* Video play overlay */}
        {sourceType === 'video' && isImageVisible && (thumbnailUrl || fallbackImageUrl) && !imageFailed && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
            <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
              <Play className="w-8 h-8 text-neutral-900 ml-1" fill="currentColor" />
            </div>
          </div>
        )}

        {/* Delete Button */}
        {onDelete && !isBulkSelectMode && (
          <button
            onClick={handleDelete}
            className="absolute top-2 left-2 p-1.5 bg-white/90 hover:bg-white text-red-600 hover:text-red-700 rounded-lg shadow-md transition-all opacity-0 group-hover:opacity-100 z-20"
          >
            <X size={14} />
          </button>
        )}

        {/* Type Badge */}
        {!isBulkSelectMode && (
          <div className="absolute top-2 right-2 z-20">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${typeColor}`}>
              {item.channel || meta.channel || sourceType.charAt(0).toUpperCase() + sourceType.slice(1)}
            </span>
          </div>
        )}

        {/* Favorite Button */}
        <button
          onClick={handleFavorite}
          disabled={togglingFavorite}
          className={`absolute bottom-2 right-2 p-2 rounded-full shadow-lg transition-all z-30 ${
            isFavorited
              ? 'bg-red-500 text-white hover:bg-red-600'
              : 'bg-white/90 text-neutral-400 hover:text-red-500 hover:bg-white'
          }`}
        >
          <Heart className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`} />
        </button>

        {/* Hover Actions */}
        {!isBulkSelectMode && (
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-3 z-25">
            <button
              onClick={(e) => {
                e.stopPropagation();
                clearChips();
                addChip({
                  id: item.id,
                  type: 'item',
                  label: item.title || item.custom_title || 'Item',
                  thumbnail: getThumbnailUrl(item),
                  file_url: item.url || item.image_url,
                  file_name: item.title || 'file',
                  file_type: item.source_type || 'unknown',
                  source: 'swirlspace',
                });
                navigate('/ai-chat');
              }}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg shadow-lg transition-all hover:scale-105 text-sm font-medium flex items-center gap-1.5"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 3l1.912 5.813a2 2 0 001.275 1.275L21 12l-5.813 1.912a2 2 0 00-1.275 1.275L12 21l-1.912-5.813a2 2 0 00-1.275-1.275L3 12l5.813-1.912a2 2 0 001.275-1.275L12 3z" />
              </svg>
              Ask Swirl
            </button>
          </div>
        )}
      </div>

      {/* Favorite Button - For items without thumbnail */}
      {!thumbnailUrl && (
        <button
          onClick={handleFavorite}
          disabled={togglingFavorite}
          className={`absolute bottom-2 right-2 p-2 rounded-full shadow-lg transition-all z-30 ${
            isFavorited
              ? 'bg-red-500 text-white hover:bg-red-600'
              : 'bg-white/90 text-neutral-400 hover:text-red-500 hover:bg-white'
          }`}
        >
          <Heart className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`} />
        </button>
      )}

      {/* Content Section */}
      <div className="p-2.5 relative flex-1 min-h-[120px] max-h-[180px] overflow-hidden flex flex-col">
        {onDelete && !thumbnailUrl && !isBulkSelectMode && (
          <button
            onClick={handleDelete}
            className="absolute top-2 left-2 p-1.5 bg-white/90 hover:bg-white text-red-600 hover:text-red-700 rounded-lg shadow-md transition-all opacity-0 group-hover:opacity-100 z-10"
          >
            <X size={14} />
          </button>
        )}

        {!thumbnailUrl && (
          <div className="mb-1.5 flex items-start gap-2">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${typeColor}`}>
              {item.channel || meta.channel || sourceType.charAt(0).toUpperCase() + sourceType.slice(1)}
            </span>
            {domain && (
              <span className="text-xs text-neutral-500 ml-auto">{domain}</span>
            )}
          </div>
        )}

        <h4 className="text-sm font-medium text-neutral-800 line-clamp-2 mb-0.5" data-decoded="true">
          {decodeHtmlEntities(item.title) || 'Untitled'}
        </h4>

        {/* AI Summary or Description - check multiple locations */}
        {(() => {
          // Priority order: AI-generated summaries first, then user description
          const summaryText = 
            item.analysis_data?.summary ||
            item.meta?.analysis_data?.summary ||
            item.meta?.ai_analysis?.summary ||
            item.ai_analysis?.summary ||
            // Only use description if it's not a generic placeholder
            (item.description && 
             !item.description.startsWith('Saved from') && 
             !item.description.startsWith('Added via') &&
             !item.description.startsWith('http') ? item.description : null);
          
          const decoded = decodeHtmlEntities(summaryText);
          // Don't show if it's a URL or generic text
          if (summaryText && 
              !summaryText.startsWith('http') && 
              !summaryText.includes('Added via') &&
              !summaryText.includes('Saved from') &&
              summaryText.length > 10) {
            return (
              <p className="text-xs text-neutral-500 line-clamp-2 mb-1.5">
                {decoded}
              </p>
            );
          }
          return null;
        })()}

        <div className="flex items-center justify-between gap-2 mt-auto">
          {(() => {
            // Get tags from multiple possible locations
            const tags = getUnifiedTags(item);
            return tags.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {tags.slice(0, 2).map((tag, i) => (
                  <span key={i} className="px-1.5 py-0.5 bg-neutral-100 text-neutral-600 text-xs rounded">
                    {tag}
                  </span>
                ))}
              </div>
            ) : (
              <div />
            );
          })()}

          {item.created_at && (
            <p className="text-xs text-neutral-400 flex-shrink-0">
              {new Date(item.created_at).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
              })}
            </p>
          )}
        </div>

        {matchReasons && matchReasons.length > 0 && (
          <div className="mt-1.5 pt-1.5 border-t border-neutral-100">
            <p className="text-[10px] text-neutral-400 leading-tight">
              {matchReasons.map((reason, i) => (
                <span key={i}>
                  {i > 0 && ' · '}
                  {reason.text}
                </span>
              ))}
            </p>
          </div>
        )}
      </div>
    </motion.article>
  );
}



